import {
  SimpleForm,
  ReferenceInput,
  SelectInput,
  TextInput,
  useNotify,
  useRedirect,
  AutocompleteInput,
  usePermissions,
} from "react-admin";
import { apiUrl } from "../apiendpoint";

import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";

const CertificateAssign = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { permissions = [] } = usePermissions();

  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState(null);
  const [name, setName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const canCreate =
    permissions.includes("certificate.create") ||
    permissions.includes("certificate.*") ||
    permissions.includes("*");

  /* 🔹 Load users */
  useEffect(() => {
    fetch(`${apiUrl}user/all`)
      .then((res) => res.json())
      .then((data) => {
        setUsers(
          data.map((u) => ({
            id: u.email,
            email: u.email,
            name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
          }))
        );
      })
      .catch(() =>
        notify("Failed to load users", { type: "error" })
      );
  }, [notify]);

  /* 🔹 Add user */
  const addUser = () => {
    if (!email) {
      notify("Please select email", { type: "warning" });
      return;
    }

    if (selectedUsers.some((u) => u.email === email)) {
      notify("User already added", { type: "warning" });
      return;
    }

    const user = users.find((u) => u.email === email);

    setSelectedUsers((prev) => [
      ...prev,
      {
        email,
        name: name || user?.name || email.split("@")[0],
      },
    ]);

    setEmail(null);
    setName("");
  };

  /* 🔹 Remove user */
  const removeUser = (emailToRemove) => {
    setSelectedUsers((prev) =>
      prev.filter((u) => u.email !== emailToRemove)
    );
  };

  /* 🔹 Submit */
  const handleSubmit = async (values) => {
    try {
      if (!selectedUsers.length) {
        notify("Add at least one user", { type: "warning" });
        return;
      }

      const formData = new FormData();
      formData.append("certificate_id", values.certificate_id);
      formData.append(
        "user_emails",
        JSON.stringify(selectedUsers)
      );
      formData.append(
        "field_values",
        JSON.stringify({
          course_name: values.course_name,
          issue_date: values.issue_date,
        })
      );

      const token = localStorage.getItem("access_token");

      const res = await fetch(`${apiUrl}assign-certificate`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed");
      }

      notify("Certificates assigned successfully 🎉", {
        type: "success",
      });

      redirect("/certificate");
    } catch (err) {
      notify(err.message, { type: "error" });
    }
  };

  /* ✅ Permission check AFTER hooks */
  if (!canCreate) return null;

  return (
    <Card>
      <CardContent>
        <SimpleForm onSubmit={handleSubmit}>
          <ReferenceInput
            source="certificate_id"
            reference="certificate"
            label="Certificate"
          >
            <SelectInput optionText="course_name" />
          </ReferenceInput>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Add Users
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={5}>
              <AutocompleteInput
                source="email_select"
                label="Email"
                choices={users}
                optionText="email"
                optionValue="email"
                value={email}
                onChange={(value) => setEmail(value)}
                fullWidth
              />
            </Grid>

            <Grid item xs={5}>
              <TextInput
                source="name_input"
                label="Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid item xs={2}>
              <IconButton color="primary" onClick={addUser}>
                <AddIcon />
              </IconButton>
            </Grid>
          </Grid>

          {selectedUsers.map((u) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={u.email}
              sx={{ mt: 1 }}
            >
              <Grid item xs={5}>{u.email}</Grid>
              <Grid item xs={5}>{u.name}</Grid>
              <Grid item xs={2}>
                <IconButton
                  color="error"
                  onClick={() => removeUser(u.email)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <TextInput source="course_name" label="Course Name" fullWidth />
          <TextInput source="issue_date" label="Issue Date" fullWidth />
        </SimpleForm>
      </CardContent>
    </Card>
  );
};

export default CertificateAssign;